import React from 'react';
import { getPrescriptionHistoryService } from '../../../services';
import PharmacyStockPicker, { PharmacyStockItem, PickedDrug, PosologyOption } from './PharmacyStockPicker';
import { toStockItems } from './pharmacyStockRows';

/**
 * SHER-567 slice 1 — everything the picker needs from the outside world, so the picker itself
 * stays presentational and the smart-field dispatcher stays untouched beyond its mount.
 *
 * The list is fetched the first time the panel opens for a given investigation and then kept:
 * what this clinician has prescribed before does not change while they write one prescription,
 * and re-fetching on every open would put a spinner in front of a clinician who is adding a
 * second batch of medications. (SHER-869: keyed on the investigation alone — there is no
 * pharmacy in this request any more.)
 *
 * The cache IS the successful result — `{ key, items }` written only in `.then`. That single
 * rule gives both retry behaviours for free rather than as special cases: a cache written only
 * on success cannot cache a failure, and a fetch abandoned before it resolves never writes one,
 * so the next open refetches in both cases. An earlier version claimed the key up front in a
 * ref and then needed two separate paths to un-claim it (on error, and on close-before-resolve);
 * both disappear here.
 *
 * `loading` deliberately stays out of the dependency array. Putting it there made setLoading(true)
 * re-trigger the effect before its own request resolved: the re-run's cleanup cancelled the
 * in-flight fetch via the `cancelled` closure, and the resolved items were silently dropped.
 */

interface Props {
    open: boolean;
    uuidInvestigation: string;
    /** The `treatment-dose`/`treatment-frecuency` slave fields' own options — see PharmacyStockPicker. */
    doseOptions: PosologyOption[];
    frequencyOptions: PosologyOption[];
    onClose: () => void;
    onConfirm: (drugs: PickedDrug[]) => void;
}

const PharmacyStockPickerContainer: React.FC<Props> = ({ open, uuidInvestigation, doseOptions, frequencyOptions, onClose, onConfirm }) => {
    const [cache, setCache] = React.useState<{ key: string; items: PharmacyStockItem[] } | null>(null);
    const [loading, setLoading] = React.useState(false);
    const key = uuidInvestigation;

    React.useEffect(() => {
        if (!open || cache?.key === key) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        getPrescriptionHistoryService(uuidInvestigation)
            .then((response: any) => {
                if (!cancelled) {
                    setCache({ key, items: toStockItems(response?.prescriptionItems ?? []) });
                }
            })
            .catch((error: any) => {
                // A history that cannot be read is an empty list, never an error screen: the
                // clinician still has the normal add-medication control right beside this one,
                // and a failed side-panel must not take the prescription form down with it.
                // Nothing is cached, so the next open retries instead of reusing the failure.
                console.error('Could not load the prescription history', error);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, key, cache, uuidInvestigation]);

    return (
        <PharmacyStockPicker
            open={open}
            items={cache?.key === key ? cache.items : []}
            loading={loading}
            doseOptions={doseOptions}
            frequencyOptions={frequencyOptions}
            onClose={onClose}
            onConfirm={onConfirm}
        />
    );
};

export default PharmacyStockPickerContainer;
